import { Rating } from '@care/react-component-lib';
import { Box, Grid, Theme, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import { seoSegmentationListingPage_seoSegmentationStats_SEOSegmentationStatsSuccess_caregiverStats as SEOSegmentationCaregiverStats } from '@/__generated__/seoSegmentationListingPage';
import formatNumToString from '@/utils/formatNumToString';
import { SEOSegmentationPageType } from '@/__generated__/globalTypes';
import { usePageAttributes } from './contexts/PageAttributesContext';

interface ReviewCardStatsProps {
  stats: SEOSegmentationCaregiverStats;
}

const numberFixTo = (value: number | null) => {
  return value?.toFixed(1);
};

const ReviewCard = ({ stats }: ReviewCardStatsProps) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { segmentNamePlural, segmentNamePluralLowerCase, city, state, pageType } =
    usePageAttributes();

  const title =
    pageType === SEOSegmentationPageType.NATIONAL
      ? `Ratings for ${segmentNamePluralLowerCase} listed on Care.com`
      : `Ratings for ${city}, ${state} ${segmentNamePluralLowerCase} listed on Care.com`;

  const description =
    pageType === SEOSegmentationPageType.NATIONAL
      ? `${segmentNamePlural} are rated <b>${numberFixTo(
          stats.avgReviewRating
        )} out of 5 stars</b> based on <b>${formatNumToString(
          stats.totalReviewCount
        )} reviews</b> of the <b>${formatNumToString(
          stats.totalProviders
        )} listed ${segmentNamePluralLowerCase}</b>.`
      : `${segmentNamePlural} in ${city}, ${state} are rated <b>${numberFixTo(
          stats.avgReviewRating
        )} out of 5 stars</b> based on <b>${formatNumToString(
          stats.totalReviewCount
        )} reviews</b> of the <b>${formatNumToString(
          stats.totalProviders
        )} listed ${segmentNamePluralLowerCase}</b>.`;

  return (
    <Grid item xs={12}>
      <Box sx={(theme) => ({ margin: theme.spacing(5, 0, 5) })}>
        <Typography variant={isMobileOrSmaller ? 'h3' : 'h2'}>{title}</Typography>
        <Typography variant="body2" my={2} mx={0}>
          Average Rating
          <Rating
            name="size-large"
            defaultValue={stats.avgReviewRating ?? undefined}
            sx={{ px: 1, py: 1, verticalAlign: 'middle' }}
            readOnly
          />
          {numberFixTo(stats.avgReviewRating)} / 5
        </Typography>
        <Typography
          variant="body2"
          color="primary"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(he.decode(description)),
          }}
        />
      </Box>
    </Grid>
  );
};

export default ReviewCard;
